import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { LandingCarrusel } from '@/components/LandingCarrusel';
import { LandingMenu } from '@/components/LandingMenu';
import { LandingTestimonios } from '@/components/LandingTestimonios';

const API = process.env.REACT_APP_BACKEND_URL;
const WA = '50254307807';
const AZUL = '#1FA3E8';
const A = 'https://customer-assets-lqy194kg.emergentagent.net/job_social-crm-3/artifacts';

const IMG = {
  logo: `${A}/ckf7btpo_c3217efc-1851-4a8d-8daf-e535de3c5dd6.jpeg`,
  hero: `${A}/taark5ga_05a00ed9-a6d1-4567-9787-8661a7e2e063.jpeg`,
  bodega: `${A}/c056shg5_9189307b-10d0-4dc8-9e04-8b5346612ce1.jpeg`,
  cancha: `${A}/6klmcn9m_6aa4fb69-0baf-45c2-a2fd-43f33214a87a.jpeg`,
  roja: `${A}/4ezxqtir_7bdf5ff3-e678-4385-bf1d-20842bb01f4e.jpeg`,
  interior: `${A}/fa2b9lmo_b764ea8f-21a7-47bb-afff-8923260709eb.jpeg`,
};

const CARRUSEL_INICIAL = [
  { url: `${A}/9czraouz_f08937ca-f8ee-4fdc-9759-6ba4b64c272d.jpeg`, caption: 'Naves industriales con techo curvo' },
  { url: `${A}/vvm7nvfk_05fe22a5-40bd-4014-9692-e6aee7278179.jpeg`, caption: 'Instalación de techo curvo' },
  { url: `${A}/qm1690k2_b1af7aca-8dc1-44ea-a19f-4415b2dd3799.jpeg`, caption: 'Bodega comercial con traslúcidos curvos' },
  { url: `${A}/6klmcn9m_6aa4fb69-0baf-45c2-a2fd-43f33214a87a.jpeg`, caption: 'Cancha deportiva techada' },
  { url: `${A}/4ezxqtir_7bdf5ff3-e678-4385-bf1d-20842bb01f4e.jpeg`, caption: 'Bodega con fachada tipo granero' },
];

const DEPARTAMENTOS = ['Guatemala', 'Sacatepéquez', 'Chimaltenango', 'Escuintla', 'Santa Rosa', 'Sololá', 'Totonicapán', 'Quetzaltenango', 'Suchitepéquez', 'Retalhuleu', 'San Marcos', 'Huehuetenango', 'Quiché', 'Baja Verapaz', 'Alta Verapaz', 'Petén', 'Izabal', 'Zacapa', 'Chiquimula', 'Jalapa', 'Jutiapa', 'El Progreso'];

const waLink = (text) => `https://wa.me/${WA}?text=${encodeURIComponent(text)}`;

const RAZONES = [
  ['Sin columnas intermedias', 'La lámina curva se sostiene por su propia forma: no necesita estructura interna ni columnas intermedias. Todo el espacio queda libre.'],
  ['Resiste viento y lluvia', 'La curvatura distribuye las cargas y deja correr el agua sin estancamientos. Ideal para el clima de Guatemala.'],
  ['Luz natural integrada', 'Incorporamos paneles traslúcidos curvos que iluminan de día y reducen tu consumo de energía.'],
  ['Rápido y económico', 'Menos estructura significa menos material y menos tiempo de obra: cubres grandes luces con una inversión menor.'],
];

export default function LandingTecho() {
  const [form, setForm] = useState({ nombre: '', telefono: '', departamento: '', detalles: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.post(`${API}/api/landing/track`, { producto: 'techo_curvo' }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.telefono.trim() || !form.departamento) {
      toast.error('Completa tu nombre, teléfono y departamento');
      return;
    }
    try {
      setSending(true);
      const res = await axios.post(`${API}/api/landing/techo/lead`, form);
      setDone(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando, intenta de nuevo');
    } finally { setSending(false); }
  };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif" }} className="min-h-screen bg-[#080B10] text-white">
      <Toaster position="top-center" richColors />
      <style>{`@keyframes deslizarX{0%,100%{transform:translateX(0)}50%{transform:translateX(10px)}}`}</style>

      <header className="sticky top-0 z-40 bg-[#080B10]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMG.logo} alt="Soluciones Arquitectónicas" className="h-10 sm:h-11 w-auto rounded" data-testid="header-logo" />
            <p className="hidden sm:block text-[10px] text-white/50 tracking-[0.25em] uppercase border-l border-white/15 pl-3">Modernizando ambientes</p>
          </div>
          <div className="flex items-center gap-2">
            <LandingMenu actual="/techocurvo" />
            <a href={waLink('¡Hola! Vi la página de techo curvo y quiero una cotización 😊')}
              target="_blank" rel="noreferrer" data-testid="wa-header-btn"
              className="bg-[#25D366] hover:brightness-110 text-white text-sm font-bold px-4 py-2 rounded-full transition-all">
              WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <img src={IMG.hero} alt="Techo curvo" className="w-full h-[64vh] object-cover object-center opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080B10] via-[#080B10]/45 to-[#080B10]/25" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-4 pb-10 w-full">
            <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-3">Grandes espacios sin columnas</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-3xl">
              Techo <span style={{ color: AZUL }}>Curvo</span>
            </h1>
            <p className="text-white/70 text-base mt-4 max-w-xl">
              Cubrimos bodegas, naves industriales, canchas, salones e iglesias con techo curvo de lámina estructural: sin columnas intermedias, con luz natural y máxima resistencia. Modernizando ambientes.
            </p>
            <div className="flex gap-3 mt-6 flex-wrap">
              <a href="#cotizar" data-testid="hero-cta"
                style={{ background: AZUL }}
                className="hover:brightness-110 text-white font-black px-6 py-3 rounded-full text-sm transition-all">
                COTIZA GRATIS →
              </a>
              <a href={waLink('¡Hola! Quiero cotizar un techo curvo 😊')} target="_blank" rel="noreferrer"
                className="border border-white/25 hover:border-white/60 text-white font-bold px-6 py-3 rounded-full text-sm transition-colors">
                Escríbenos por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section style={{ background: AZUL }} data-testid="stats-strip">
        <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            ['100%', 'del espacio libre, sin columnas intermedias'],
            ['Grandes luces', 'cubre bodegas, canchas y salones completos'],
            ['Luz natural', 'con paneles traslúcidos curvos'],
            ['+18 años', 'de experiencia instalando'],
          ].map(([n, t]) => (
            <div key={n}>
              <p className="text-2xl sm:text-3xl font-black leading-none">{n}</p>
              <p className="text-white/90 text-xs sm:text-sm font-medium mt-1">{t}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Por qué techo curvo */}
      <section className="max-w-6xl mx-auto px-4 py-14" data-testid="seccion-porque">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-3">Ingeniería inteligente</p>
            <h2 className="text-2xl sm:text-3xl font-black leading-tight">¿Por qué elegir <span style={{ color: AZUL }}>techo curvo</span>?</h2>
            <p className="text-white/60 text-base mt-4">
              El techo curvo es la forma más eficiente de cubrir grandes espacios: la propia curvatura de la lámina hace el trabajo estructural, eliminando costos de vigas, columnas y mantenimiento.
            </p>
            <a href="#cotizar" style={{ background: AZUL }}
              className="inline-block mt-6 hover:brightness-110 text-white font-black px-6 py-3 rounded-full text-sm transition-all">
              QUIERO MI TECHO →
            </a>
          </div>
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {RAZONES.map(([t, d], i) => (
              <div key={t} className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#1FA3E8]/40 transition-colors" data-testid={`razon-${i}`}>
                <p style={{ color: AZUL }} className="text-2xl font-black leading-none mb-2">{String(i + 1).padStart(2, '0')}</p>
                <p className="font-black text-sm sm:text-base">{t}</p>
                <p className="text-white/55 text-sm mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usos */}
      <section className="max-w-6xl mx-auto px-4 pb-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-testid="seccion-usos">
        <figure className="rounded-3xl overflow-hidden border border-white/10">
          <img src={IMG.bodega} alt="Bodega con techo curvo" className="w-full h-[420px] object-cover object-center" />
        </figure>
        <div>
          <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-3">Para cada proyecto</p>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight">Un techo para <span style={{ color: AZUL }}>todo uso</span></h2>
          <div className="grid grid-cols-2 gap-2 mt-5 text-sm font-bold text-white/75">
            {['Bodegas y naves industriales', 'Canchas deportivas', 'Iglesias y salones de eventos', 'Parqueos y patios', 'Mercados y comercios', 'Escuelas y gimnasios'].map((u) => (
              <p key={u} className="flex items-center gap-2"><span style={{ color: AZUL }} className="font-black">✓</span>{u}</p>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <img src={IMG.cancha} alt="Cancha deportiva con techo curvo" className="rounded-2xl h-32 w-full object-cover border border-white/10" />
            <img src={IMG.roja} alt="Bodega comercial con techo curvo" className="rounded-2xl h-32 w-full object-cover border border-white/10" />
          </div>
        </div>
      </section>

      {/* Carrusel */}
      <section className="pb-14" data-testid="carrusel-proyectos">
        <div className="max-w-6xl mx-auto px-4 flex items-end justify-between gap-4">
          <div>
            <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-2">Proyectos reales</p>
            <h2 className="text-2xl sm:text-3xl font-black mb-6">Inspírate para tu <span style={{ color: AZUL }}>proyecto</span></h2>
          </div>
          <p className="text-white/60 text-sm font-bold mb-6 shrink-0 flex items-center gap-1" data-testid="deslizar-hint">
            Deslizar <span style={{ color: AZUL, animation: 'deslizarX 1.2s ease-in-out infinite', display: 'inline-block' }} className="font-black text-lg">→</span>
          </p>
        </div>
        <LandingCarrusel producto="techo_curvo" initial={CARRUSEL_INICIAL} />
      </section>

      {/* Iluminación + guía PDF */}
      <section className="max-w-6xl mx-auto px-4 pb-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-3">¡Somos Profesionales!</p>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight">Luz natural y décadas de <span style={{ color: AZUL }}>vida útil</span></h2>
          <p className="text-white/60 text-base mt-4">
            Integramos paneles traslúcidos curvos que llenan de luz tu espacio y bajan el consumo eléctrico. Y con el mantenimiento correcto, tu techo curvo dura décadas sin problemas.
          </p>
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 mt-5" data-testid="guia-card">
            <p className="font-black text-sm">📘 Manual de Mantenimiento GRATIS</p>
            <p className="text-white/55 text-sm mt-1.5">
              Al comprar con nosotros recibes el manual para cuidar tu techo: limpieza de canales, revisión de tornillería y sellos, y qué hacer después de tormentas o granizo.
            </p>
            <a href={`${API}/api/landing/guia-techo-curvo`} data-testid="guia-download-btn"
              className="inline-block mt-3 border border-[#1FA3E8] text-[#1FA3E8] hover:bg-[#1FA3E8] hover:text-white font-black px-5 py-2.5 rounded-full text-xs transition-colors">
              DESCARGAR MANUAL PDF
            </a>
          </div>
        </div>
        <figure className="rounded-3xl overflow-hidden border border-white/10">
          <img src={IMG.interior} alt="Interior de nave con techo curvo y traslúcidos" className="w-full h-[440px] object-cover object-center" />
          <figcaption className="bg-black/60 text-xs text-white/70 px-4 py-2 font-bold">Interior con paneles traslúcidos: luz natural todo el día</figcaption>
        </figure>
      </section>

      {/* Testimonios */}
      <section className="max-w-6xl mx-auto px-4 pb-14" data-testid="seccion-testimonios">
        <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-2 text-center">Lo que dicen nuestros clientes</p>
        <h2 className="text-2xl sm:text-3xl font-black text-center mb-8">Clientes que confiaron <span style={{ color: AZUL }}>en nosotros</span></h2>
        <LandingTestimonios producto="techo_curvo" initial={[
            {
              nombre: 'Cooperativa San Miguel',
              texto: 'Techamos nuestra bodega de granos de 15 metros de luz sin una sola columna. El espacio libre nos cambió la operación: los camiones maniobran adentro sin problema.',
            },
            {
              nombre: 'Pastor Daniel Morales',
              texto: 'Para el templo necesitábamos cubrir un salón grande con buen presupuesto. El techo curvo fue la solución: rápido de instalar, se ve imponente y con los traslúcidos casi no encendemos luces de día.',
            },
            {
              nombre: 'Ing. Rodrigo Paz',
              texto: 'He contratado sus techos curvos para dos proyectos industriales. Buen cálculo, lámina de calidad y cumplen el cronograma. Los recomiendo sin duda.',
            },
          ]} color={AZUL} />
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 pb-14" data-testid="seccion-faq">
        <p style={{ color: AZUL }} className="font-bold text-xs tracking-[0.3em] uppercase mb-2 text-center">Resolvemos tus dudas</p>
        <h2 className="text-2xl sm:text-3xl font-black text-center mb-8">Preguntas <span style={{ color: AZUL }}>frecuentes</span></h2>
        <div className="space-y-3">
          {[
            ['¿Cuánto cuesta un techo curvo?',
              'Depende del ancho (luz) a cubrir, el largo y la altura del proyecto. La cotización es GRATIS — envíanos las medidas del terreno o local por WhatsApp y te respondemos el mismo día.'],
            ['¿Qué tan grande puede ser el espacio a cubrir?',
              'El techo curvo cubre grandes luces sin columnas intermedias, ideal para bodegas, canchas y salones completos. Evaluamos tu caso y te confirmamos la mejor solución técnica.'],
            ['¿Necesita estructura o columnas en medio?',
              'No. La lámina curva estructural se sostiene por su propia forma, apoyada solo en los muros o vigas laterales. Todo el espacio interior queda libre.'],
            ['¿Puede llevar iluminación natural?',
              'Sí, integramos paneles traslúcidos curvos en el techo que iluminan el interior de día y reducen el gasto de energía eléctrica.'],
            ['¿Cómo empiezo mi proyecto?',
              'Llena el formulario de esta página o escríbenos al WhatsApp 5430-7807. Vamos, medimos, te cotizamos y coordinamos la instalación de principio a fin.'],
          ].map(([q, a], i) => (
            <details key={q} className="group bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between gap-4 cursor-pointer px-5 py-4 font-bold text-sm sm:text-base list-none [&::-webkit-details-marker]:hidden hover:bg-white/[0.03] transition-colors">
                {q}
                <span style={{ color: AZUL }} className="text-xl font-black shrink-0 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="px-5 pb-5 text-white/65 text-sm leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Formulario */}
      <section id="cotizar" className="max-w-2xl mx-auto px-4 pb-16">
        <div className="bg-white/[0.05] border border-white/10 rounded-3xl p-6 sm:p-8">
          {done ? (
            <div className="text-center py-6" data-testid="lead-success">
              <p className="text-5xl mb-4">✅</p>
              <h3 className="text-2xl font-black">¡Recibido, {form.nombre.split(' ')[0]}!</h3>
              <p className="text-white/60 text-sm mt-2 mb-6">Un asesor te contactará muy pronto. Si quieres respuesta inmediata, continúa por WhatsApp:</p>
              <a href={done.wa_link} target="_blank" rel="noreferrer" data-testid="lead-wa-btn"
                className="inline-block bg-[#25D366] hover:brightness-110 text-white font-black px-8 py-3 rounded-full text-sm transition-all">
                💬 Continuar en WhatsApp
              </a>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-black">Cotiza tu techo curvo <span style={{ color: AZUL }}>GRATIS</span></h2>
              <p className="text-white/50 text-sm mt-1 mb-6">Sin compromiso. Te contactamos por WhatsApp o llamada.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">Tu nombre *</label>
                  <input value={form.nombre} onChange={set('nombre')} data-testid="lead-nombre" placeholder="Ej: Carlos Pérez"
                    className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">Teléfono / WhatsApp *</label>
                  <input value={form.telefono} onChange={set('telefono')} data-testid="lead-telefono" placeholder="Ej: 5555 5555" inputMode="tel"
                    className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors" />
                </div>
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">¿En qué departamento estás? *</label>
                  <select value={form.departamento} onChange={set('departamento')} data-testid="lead-departamento" style={{ colorScheme: 'dark' }}
                    className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors">
                    <option value="">Selecciona…</option>
                    {DEPARTAMENTOS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-wide">Detalles de tu proyecto</label>
                  <textarea value={form.detalles} onChange={set('detalles')} data-testid="lead-detalles" rows={3}
                    placeholder="Ej: bodega de 12 x 30 metros, techo curvo con traslúcidos…"
                    className="mt-1 w-full bg-black/30 border border-white/15 focus:border-[#1FA3E8] rounded-xl px-4 py-3 text-sm text-white outline-none transition-colors resize-none" />
                </div>
                <button type="submit" disabled={sending} data-testid="lead-submit"
                  style={{ background: AZUL }}
                  className="w-full hover:brightness-110 disabled:opacity-60 text-white font-black py-3.5 rounded-xl text-sm transition-all">
                  {sending ? 'Enviando…' : 'QUIERO MI COTIZACIÓN GRATIS'}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10 py-6 text-center text-xs text-white/40">
        Soluciones Arquitectónicas · Modernizando ambientes · Grupo SACH · Guatemala · WhatsApp +502 5430 7807 · +18 años de experiencia
      </footer>

      <a href={waLink('¡Hola! Vi la página de techo curvo y quiero una cotización 😊')}
        target="_blank" rel="noreferrer" data-testid="wa-float-btn" aria-label="WhatsApp"
        className="fixed bottom-5 right-5 z-50 bg-[#25D366] hover:scale-110 transition-transform w-14 h-14 rounded-full flex items-center justify-center shadow-2xl">
        <svg viewBox="0 0 32 32" width="30" height="30" fill="#fff"><path d="M16 3C9.4 3 4 8.4 4 15c0 2.1.6 4.2 1.7 6L4 29l8.2-1.6c1.2.6 2.5.9 3.8.9 6.6 0 12-5.4 12-12S22.6 3 16 3zm0 22c-1.2 0-2.4-.3-3.5-.8l-.6-.3-4.9 1 1-4.7-.3-.6C6.6 18.1 6 16.6 6 15 6 9.5 10.5 5 16 5s10 4.5 10 10-4.5 10-10 10zm5.5-7.5c-.3-.2-1.8-.9-2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6l-1-2.3c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.4z"/></svg>
      </a>
    </div>
  );
}
